import React from "react";
import { block, C, Icon, Eyebrow, MultilineText } from "./shared";

/* a single floating knowledge node, drifting like a hero frame */
function MemNode({ label, icon, color, bg, x, y, dur, fd }) {
  return (
    <div style={{ position: "absolute", left: x, top: y, transform: "translate(-50%,-50%)", zIndex: 3 }}>
      <div className="fr-floaty" style={{ "--d": `${dur}s`, animationDelay: `${fd}s` }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px", borderRadius: "var(--radius-pill)", background: bg, color, fontSize: 12.5, fontWeight: 600, fontFamily: "var(--font-body)", whiteSpace: "nowrap", boxShadow: "0 6px 18px -8px rgba(20,19,16,0.7)", border: "1px solid rgba(250,246,238,0.14)" }}>
          <Icon name={icon} size={14} color={color} />{label}
        </span>
      </div>
    </div>
  );
}

function MemoryField() {
  const cx = 220, cy = 156;
  const L = C.memoryViz;
  const nodes = [
    { label: L.nodes.characters, icon: "users-round", color: "var(--sage-2)", bg: "rgba(151,160,146,0.16)", x: 92, y: 60, dur: 6.6, fd: 0 },
    { label: L.nodes.locations, icon: "map-pin", color: "var(--river-2)", bg: "rgba(159,180,190,0.16)", x: 350, y: 54, dur: 7.4, fd: 0.6 },
    { label: L.nodes.scripts, icon: "file-text", color: "var(--ochre-2)", bg: "rgba(227,195,133,0.16)", x: 44, y: 164, dur: 7.0, fd: 1.1 },
    { label: L.nodes.style, icon: "palette", color: "var(--mauve-2)", bg: "rgba(178,160,176,0.18)", x: 388, y: 172, dur: 6.8, fd: 0.3 },
    { label: L.nodes.references, icon: "image", color: "var(--river-2)", bg: "rgba(159,180,190,0.16)", x: 96, y: 270, dur: 7.6, fd: 0.9 },
    { label: L.nodes.shots, icon: "clapperboard", color: "var(--sage-2)", bg: "rgba(151,160,146,0.16)", x: 346, y: 272, dur: 6.4, fd: 1.4 },
  ];
  return (
    <div className="mem-scene reveal" style={{ "--i": 1 }}>
      {/* converging currents — knowledge flowing into the core */}
      <svg viewBox="0 0 440 330" width="440" height="330" aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, overflow: "visible" }}>
        {nodes.map((n) => (
          <line key={n.label} className="mem-beam" x1={n.x} y1={n.y} x2={cx} y2={cy}
            stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round"
            strokeDasharray="2 9" opacity="0.5" />
        ))}
        {nodes.map((n) => (
          <circle key={n.label + "d"} cx={n.x} cy={n.y} r="2.6" fill="var(--accent)" opacity="0.7" />
        ))}
      </svg>

      {/* sonar rings radiating from the living core */}
      {[0, 1.45, 2.9].map((d) => (
        <span key={d} className="mem-ring" aria-hidden="true" style={{ position: "absolute", left: cx, top: cy, width: 184, height: 184, borderRadius: "50%", border: "1px solid var(--accent)", animationDelay: `${d}s`, zIndex: 1 }}></span>
      ))}

      {/* floating knowledge nodes */}
      {nodes.map((n) => <MemNode key={n.label} {...n} />)}

      {/* the core — one growing source of truth */}
      <div className="mem-core fr-sweep" style={{ position: "absolute", left: cx, top: cy, transform: "translate(-50%,-50%)", zIndex: 4, width: 198, padding: "16px 18px", borderRadius: "var(--radius-lg)", background: "linear-gradient(180deg, var(--ink-raised) 0%, var(--surface-ink) 100%)", border: "1px solid rgb(var(--glow-rgb) / 0.22)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
          <span style={{ display: "inline-flex", width: 30, height: 30, alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-sm)", background: "rgba(227,195,133,0.16)", flex: "none" }}>
            <Icon name="brain" size={18} color="var(--ochre-2)" />
          </span>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--ink-text-display)", lineHeight: 1.1 }}>{L.coreTitle}</span>
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--ink-text-muted)", lineHeight: 1.45 }}>{L.coreBody}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontFamily: "var(--font-mono)", fontSize: 11 }}>
          {L.tags.map((w, i) => (
            <span key={w} style={{ padding: "3px 8px", borderRadius: "var(--radius-xs)", background: "rgba(250,246,238,0.06)", color: ["var(--sage-2)", "var(--river-2)", "var(--ochre-2)"][i % 3] }}>{w}</span>
          ))}
        </div>
      </div>

    </div>
  );
}

export function ContentMemory() {
  const b = block("memory");
  if (!b) return null;
  return (
    <section id="memory" style={{ margin: "72px clamp(14px,3vw,28px)", borderRadius: "var(--radius-xl)", background: "var(--surface-ink)", border: "1px solid var(--ink-border)", overflow: "hidden", position: "relative", scrollMarginTop: 76 }} data-screen-label="Content Memory">
      {/* faint clay weave, same texture language as the hero */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", backgroundImage: "radial-gradient(var(--ink-dot) 1px, transparent 1.4px)", backgroundSize: "26px 26px", maskImage: "radial-gradient(120% 90% at 78% 38%, #000 12%, transparent 70%)", WebkitMaskImage: "radial-gradient(120% 90% at 78% 38%, #000 12%, transparent 70%)" }}></div>
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(28px,4vw,64px)", alignItems: "center", padding: "clamp(40px,5vw,72px)" }} className="fr-2col">
        <div className="reveal">
          <Eyebrow light>{b.eyebrow}</Eyebrow>
          <h2 style={{ fontSize: "clamp(30px,3.6vw,46px)", color: "var(--ink-text-display)", margin: "14px 0 20px", letterSpacing: "-0.025em", lineHeight: 1.06, textWrap: "balance", maxWidth: 480 }}>
            <MultilineText text={b.title} />
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.66, color: "var(--ink-text-body)", margin: "0 0 18px", maxWidth: 480 }}>
            {b.body1}
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.66, color: "var(--ink-text-muted)", margin: 0, maxWidth: 480 }}>
            {b.body2}
          </p>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <MemoryField />
        </div>
      </div>
    </section>
  );
}
